import React, { useState, useRef, useEffect } from 'react';
import {
  Smartphone,
  Users,
  FileText,
  DollarSign,
  MoreHorizontal,
  Calendar,
  X
} from 'lucide-react';
import { playPulseSeal } from '../utils/pulseAudio';
import { UserSession, UserRole } from '../types';
import { MobileAccountRoleSection } from './MobileAccountRoleSection';

interface MobileAssistantBottomNavProps {
  currentTab: string;
  onSelectTab: (tab: string) => void;
  user?: UserSession;
  onSwitchRole?: (role: UserRole) => void;
  onLogout?: () => void;
}

interface NavDestination {
  id: string;
  label: string;
  tab: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number; size?: number }>;
  isActive: (current: string) => boolean;
  activeColor: {
    text: string;
    icon: string;
    indicator: string;
    glow: string;
  };
}

export const MobileAssistantBottomNav: React.FC<MobileAssistantBottomNavProps> = ({
  currentTab,
  onSelectTab,
  user,
  onSwitchRole,
  onLogout
}) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreSheetRef = useRef<HTMLDivElement>(null);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleTouchOutside = (e: MouseEvent | TouchEvent) => {
      if (moreSheetRef.current && !moreSheetRef.current.contains(e.target as Node)) {
        setIsMoreOpen(false);
      }
    };
    if (isMoreOpen) {
      document.addEventListener('mousedown', handleTouchOutside);
      document.addEventListener('touchstart', handleTouchOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleTouchOutside);
      document.removeEventListener('touchstart', handleTouchOutside);
    };
  }, [isMoreOpen]);

  const destinations: NavDestination[] = [
    {
      id: 'mobile-nav-soap',
      label: 'SOAP',
      tab: 'mobile-soap',
      icon: Smartphone,
      isActive: (cur) => cur === 'mobile-soap',
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    },
    {
      id: 'mobile-nav-clients',
      label: 'Clients',
      tab: 'clients',
      icon: Users,
      isActive: (cur) => cur === 'clients',
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    },
    {
      id: 'mobile-nav-documents',
      label: 'Documents',
      tab: 'clinical',
      icon: FileText,
      isActive: (cur) => cur === 'clinical',
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    },
    {
      id: 'mobile-nav-more',
      label: 'More',
      tab: 'more',
      icon: MoreHorizontal,
      isActive: (cur) => cur === 'assistant' || cur === 'assignments' || cur === 'my-pay' || isMoreOpen,
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    }
  ];

  const handleTap = (tab: string) => {
    playPulseSeal({ cooldownMs: 120, volume: 0.35 });
    if (tab === 'more') {
      setIsMoreOpen(!isMoreOpen);
    } else {
      setIsMoreOpen(false);
      onSelectTab(tab);
    }
  };

  const handleSelectMoreOption = (tab: string) => {
    playPulseSeal({ cooldownMs: 120, volume: 0.35 });
    setIsMoreOpen(false);
    onSelectTab(tab);
  };

  return (
    <>
      {/* More Action Sheet Modal */}
      {isMoreOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-xs md:hidden animate-in fade-in duration-150">
          <div
            ref={moreSheetRef}
            className="absolute bottom-[calc(52px+env(safe-area-inset-bottom,0px))] inset-x-3 bg-[#f8fafc] rounded-2xl border border-slate-200/90 shadow-[0_-8px_30px_rgba(15,23,42,0.12),0_4px_16px_rgba(15,23,42,0.08)] p-3.5 space-y-2.5 animate-in slide-in-from-bottom-3 duration-200"
          >
            <div className="relative flex items-center justify-between pb-2.5 border-b border-slate-200/90 px-1 min-h-[36px]">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-1.5 h-4.5 rounded-full bg-slate-700 shrink-0" aria-hidden="true" />
                <div className="flex items-baseline gap-2 truncate">
                  <h2 className="text-sm font-bold tracking-tight text-slate-900 leading-none">
                    Assistant Hub
                  </h2>
                  <span className="text-[10px] font-mono font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 leading-none shrink-0">
                    More
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsMoreOpen(false)}
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 border border-slate-200/90 transition-colors cursor-pointer shrink-0"
                aria-label="Close Assistant Hub"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Account & Role Section */}
            {user && (
              <MobileAccountRoleSection
                user={user}
                onSwitchRole={onSwitchRole}
                onLogout={onLogout}
                onAfterAction={() => setIsMoreOpen(false)}
              />
            )}

            <div className="grid grid-cols-1 gap-2 pt-1">
              {/* Assistant AI */}
              <button
                type="button"
                id="assistant-more-ai"
                onClick={() => handleSelectMoreOption('assistant')}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all text-left cursor-pointer active:scale-[0.99] ${
                  currentTab === 'assistant'
                    ? 'bg-slate-100 border border-slate-300 text-slate-950 shadow-xs'
                    : 'bg-white hover:bg-slate-50/80 border border-slate-200/90 text-slate-800 shadow-xs'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 shrink-0">
                    <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-5 h-5 shrink-0 object-contain" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>Assistant AI</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200 font-semibold">
                        Clinical AI
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      Goal tracking & clinical documentation assistance
                    </div>
                  </div>
                </div>
              </button>

              {/* Visit Planner */}
              <button
                type="button"
                id="assistant-more-planner"
                onClick={() => handleSelectMoreOption('assignments')}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all text-left cursor-pointer active:scale-[0.99] ${
                  currentTab === 'assignments'
                    ? 'bg-slate-100 border border-slate-300 text-slate-950 shadow-xs'
                    : 'bg-white hover:bg-slate-50/80 border border-slate-200/90 text-slate-800 shadow-xs'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 shrink-0">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>Visit Planner</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200 font-semibold">
                        Schedule
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      View client visits, schedule & daily route
                    </div>
                  </div>
                </div>
              </button>

              {/* My Pay */}
              <button
                type="button"
                id="assistant-more-my-pay"
                onClick={() => handleSelectMoreOption('my-pay')}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all text-left cursor-pointer active:scale-[0.99] ${
                  currentTab === 'my-pay'
                    ? 'bg-slate-100 border border-slate-300 text-slate-950 shadow-xs'
                    : 'bg-white hover:bg-slate-50/80 border border-slate-200/90 text-slate-800 shadow-xs'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 shrink-0">
                    <DollarSign className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>My Pay</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-emerald-100/70 text-emerald-700 border border-emerald-200 font-semibold">
                        Earnings
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      Timesheets, compensation & pay breakdown
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      <nav
        id="pulse-mobile-assistant-bottom-nav"
        aria-label="Assistant Mobile Navigation"
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 border-t border-slate-200/90 shadow-[0_-2px_10px_rgba(15,23,42,0.06)] select-none backdrop-blur-md"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* 4 Primary Destination Grid */}
        <div className="grid grid-cols-4 h-[52px] items-stretch">
          {destinations.map((item) => {
            const active = item.isActive(currentTab);
            const Icon = item.icon;

            return (
              <button
                key={item.id}
                id={item.id}
                type="button"
                onClick={() => handleTap(item.tab)}
                className="relative flex flex-col items-center justify-center h-full py-1 px-1 transition-all duration-150 active:scale-90 group focus:outline-none cursor-pointer"
                aria-current={active ? 'page' : undefined}
              >
                {/* Active top edge indicator bar */}
                {active && (
                  <div
                    className={`absolute top-0 h-[2.5px] w-8 rounded-full ${item.activeColor.indicator} transition-all duration-200`}
                  />
                )}

                {/* Icon */}
                <div className="relative flex items-center justify-center shrink-0">
                  <Icon
                    className={`w-5 h-5 transition-all duration-150 ${
                      active
                        ? `${item.activeColor.icon} scale-105`
                        : 'text-slate-500 group-hover:text-slate-700'
                    }`}
                    strokeWidth={active ? 2.3 : 1.9}
                  />
                </div>

                {/* Destination Label */}
                <span
                  className={`text-[10px] tracking-tight leading-none mt-1 whitespace-nowrap transition-colors select-none ${
                    active
                      ? `font-bold ${item.activeColor.text}`
                      : 'font-medium text-slate-500 group-hover:text-slate-700'
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};
